import { Chip, Container, Stack, Tooltip } from '@mui/material';
import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import { Page } from '../../../components';
import AreYouSureModal from '../../../components/are-you-sure-modal';
import HeaderBreadcrumbs from '../../../components/header-breadcrumbs';
import { downloadCsv } from '../../../hooks/useCsvDownload';
import { permissionCheck } from '../../../helpers/permissionCheck';
import { customerPermissionFlags } from '../../../constants/systemPermissions.constants';
import { TEST_MODE_COPY } from './testMode.copy';
import { testModeFixturesEnabled } from './testMode.fixtures';
import {
    useBulkReEnroll,
    useRequestExit,
    useTestModeKpiCounts,
    useTestModeStatusList,
} from './testMode.api';
import {
    EMPTY_FILTER_VALUES,
    StatusListParams,
    TestModeFilterValues,
    TestModeKpiKey,
    deriveRowStatus,
    filtersToParams,
    hasAnyFilter,
} from './testMode.types';
import { KpiTiles } from './components/KpiTiles';
import { ProgressReport } from './components/ProgressReport';
import { StatusTable } from './components/StatusTable';
import { TestModeFilters } from './components/TestModeFilters';

type PendingAction =
    | { kind: 'request-exit'; userIds: string[]; title: string; body: string }
    | { kind: 'enroll'; userIds: string[]; title: string; body: string };

/**
 * Safety Hub Test Mode tab (TM-8): summary KPI tiles, the filterable
 * test-status user list with bulk re-enroll / request-to-exit actions, and
 * the onboarding-progress report.
 *
 * KPI tiles double as cohort filters and combine with the filter bar; where
 * both constrain the same dimension, the tile wins (it was clicked last in
 * the design's mental model, and selection resets on Apply anyway).
 */
const KPI_PARAMS: Record<TestModeKpiKey, Partial<StatusListParams>> = {
    inTestMode: { status: 'in-test' },
    completed: { testCompleted: true },
    notTested: { status: 'in-test', testCompleted: false },
    flagged: { status: 'flagged' },
};

export const TestModeScreen: React.FC = () => {
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [kpiFilter, setKpiFilter] = useState<TestModeKpiKey | null>(null);
    const [filters, setFilters] = useState<TestModeFilterValues>(EMPTY_FILTER_VALUES);
    const [selected, setSelected] = useState<Record<string, boolean>>({});
    // Bumping this key remounts the filter bar, resetting its internal
    // draft/applied state when filters are cleared from outside it (the
    // empty state's "Clear filters" button).
    const [filtersKey, setFiltersKey] = useState(0);

    const params = useMemo<StatusListParams>(
        () => ({
            page,
            perPage,
            ...filtersToParams(filters),
            ...(kpiFilter ? KPI_PARAMS[kpiFilter] : {}),
        }),
        [page, perPage, filters, kpiFilter],
    );

    const list = useTestModeStatusList(params);
    const { counts, loading: kpiLoading } = useTestModeKpiCounts();

    // Rows a request-exit was just sent for — reflected immediately as the
    // "Exit requested" chip. Server-side persistence for this state is still
    // an open question with Evan; until then this is per-session only.
    const [exitRequestedIds, setExitRequestedIds] = useState<Set<string>>(new Set());
    const [pending, setPending] = useState<PendingAction | null>(null);

    const requestExit = useRequestExit();
    const bulkEnroll = useBulkReEnroll();
    const actionLoading = requestExit.isLoading || bulkEnroll.isLoading;
    const canBulkEnroll = permissionCheck(customerPermissionFlags.ADMIN);

    const rawRows = list.data?.items ?? [];
    const rows = useMemo(
        () => rawRows.map((r) => (exitRequestedIds.has(r.userId) ? { ...r, exitRequested: true } : r)),
        [rawRows, exitRequestedIds],
    );
    const total = list.data?.total ?? 0;

    const toggleKpi = (key: TestModeKpiKey) => {
        setKpiFilter((cur) => (cur === key ? null : key));
        setPage(1);
        setSelected({});
    };

    const applyFilters = (next: TestModeFilterValues) => {
        setFilters(next);
        setPage(1);
        setSelected({});
    };

    const clearAllFilters = () => {
        setFilters(EMPTY_FILTER_VALUES);
        setKpiFilter(null);
        setPage(1);
        setFiltersKey((k) => k + 1);
    };

    const selectedIds = Object.keys(selected).filter((id) => selected[id]);

    const confirmPending = async () => {
        if (!pending || actionLoading) return;
        const { kind, userIds } = pending;
        const A = TEST_MODE_COPY.actions;
        try {
            if (kind === 'request-exit') {
                const outcome = await requestExit.mutateAsync(userIds);
                setExitRequestedIds((prev) => new Set([...Array.from(prev), ...outcome.ok]));
                if (outcome.failed.length === 0) toast.success(A.toastExitRequested(outcome.ok.length));
                else if (outcome.ok.length === 0) toast.error(A.toastAllFailed);
                else toast.warn(A.toastPartial(outcome.ok.length, outcome.failed.length));
            } else {
                const outcome = await bulkEnroll.mutateAsync(userIds);
                const ok = outcome.results.filter((r) => r.ok).length;
                const failed = outcome.results.length - ok + outcome.rejected.length;
                if (failed === 0) toast.success(A.toastEnrolled(ok));
                else if (ok === 0) toast.error(A.toastAllFailed);
                else toast.warn(A.toastPartial(ok, failed));
            }
            setSelected({});
        } catch (err) {
            console.error('[TestMode] action failed', err);
            toast.error(TEST_MODE_COPY.actions.toastAllFailed);
        } finally {
            setPending(null);
        }
    };

    const exportRoster = () => {
        try {
            downloadCsv(
                rows.map((r) => ({
                    name: r.name,
                    employeeId: r.employeeId ?? '',
                    group: r.group ?? '',
                    team: r.team ?? '',
                    status: deriveRowStatus(r),
                    appTested: r.testedApp ? 'yes' : 'no',
                    deviceTested: r.testedDevice ? 'yes' : 'no',
                    lastEnteredAt: r.lastEnteredAt ?? '',
                    expiresAt: r.expiresAt ?? '',
                })),
                `${TEST_MODE_COPY.csvFileName}-${dayjs().format('YYYY-MM-DD')}`,
            );
        } catch (err) {
            console.error('[TestMode] roster CSV export failed', err);
            toast.error(TEST_MODE_COPY.errors.exportCsv);
        }
    };

    // "Nothing to nudge": there are users in scope and none of them are
    // sitting untested. Flag data is excluded until the backend exposes it.
    const allClear =
        !kpiLoading && (counts.inTestMode ?? 0) + (counts.completed ?? 0) > 0 && counts.notTested === 0;

    return (
        <Page title={TEST_MODE_COPY.pageTitle}>
            <Container maxWidth={false}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <HeaderBreadcrumbs
                        heading={TEST_MODE_COPY.heading}
                        links={[
                            {
                                name: TEST_MODE_COPY.description,
                                href: '/test-mode',
                            },
                        ]}
                        activeLast={false}
                    />
                    {testModeFixturesEnabled() && (
                        <Tooltip title="Dev-only sample rows — localStorage.removeItem('testModeFixtures') to disable" arrow>
                            <Chip label="Sample data" color="warning" size="small" sx={{ fontWeight: 600 }} />
                        </Tooltip>
                    )}
                </Stack>

                <KpiTiles counts={counts} loading={kpiLoading} active={kpiFilter} onToggle={toggleKpi} />

                <Stack gap={2}>
                    <TestModeFilters key={filtersKey} onApply={applyFilters} onExport={exportRoster} />

                    <StatusTable
                        rows={rows}
                        total={total}
                        page={page}
                        perPage={perPage}
                        loading={list.isLoading}
                        loadError={Boolean(list.isError)}
                        hasActiveFilters={kpiFilter !== null || hasAnyFilter(filters)}
                        allClear={allClear}
                        selected={selected}
                        canBulkEnroll={canBulkEnroll}
                        onToggleRow={(id) => setSelected((s) => ({ ...s, [id]: !s[id] }))}
                        onToggleAllOnPage={(ids, select) =>
                            setSelected((s) => {
                                const next = { ...s };
                                ids.forEach((id) => { next[id] = select; });
                                return next;
                            })
                        }
                        onClearSelection={() => setSelected({})}
                        onPageChange={setPage}
                        onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                        onClearFilters={clearAllFilters}
                        onBulkRequestExit={() =>
                            setPending({
                                kind: 'request-exit',
                                userIds: selectedIds,
                                title: TEST_MODE_COPY.actions.bulkRequestExitTitle(selectedIds.length),
                                body: TEST_MODE_COPY.actions.bulkRequestExitBody,
                            })
                        }
                        onBulkEnroll={() =>
                            setPending({
                                kind: 'enroll',
                                userIds: selectedIds,
                                title: TEST_MODE_COPY.actions.bulkEnrollTitle(selectedIds.length),
                                body: TEST_MODE_COPY.actions.bulkEnrollBody,
                            })
                        }
                        onRequestExit={(row) =>
                            setPending({
                                kind: 'request-exit',
                                userIds: [row.userId],
                                title: TEST_MODE_COPY.actions.requestExitTitle,
                                body: TEST_MODE_COPY.actions.requestExitBody(row.name),
                            })
                        }
                    />
                </Stack>

                <ProgressReport />

                <AreYouSureModal
                    visible={pending !== null}
                    loading={actionLoading}
                    title={pending?.title ?? ''}
                    description={pending?.body ?? ''}
                    color="primary"
                    onConfirm={confirmPending}
                    onHide={() => { if (!actionLoading) setPending(null); }}
                />
            </Container>
        </Page>
    );
};
